import {
  makeDataState,
  makeListState,
  concatUnique,
  STATUSES,
} from 'app/utils/redux';

import { AD_TYPES, AD_STATUSES } from 'app/utils/constants';

import snakeize from 'snakeize';

import * as t from './types';

const initialState = {
  accessToken: null,
  firebaseIdToken: null,
  locale: 'en_US',
  timezone: 'Asia/Seoul',
  isLoading: false,
  user: makeDataState(),
  platform: null,
  version: null,
  sessionId: null,
  isPushEnabled: false,
  isStorageWarningShow: false,
  abTest: {
    isDataReceived: null,
  },
  adLoadStatus: {
    [AD_TYPES.REWARD]: AD_STATUSES.LOADING,
    [AD_TYPES.CHEER_REWARD]: AD_STATUSES.LOADING,
    [AD_TYPES.INTERSTITIAL]: AD_STATUSES.LOADING,
    [AD_TYPES.INTERSTITIAL_BACKFILL]: AD_STATUSES.LOADING,
    [AD_TYPES.FBINTERSTITIAL]: AD_STATUSES.LOADING,
  },
  isLimitedWeb: false,
  promotions: {
    dailyBalloonPromotion: {},
  },
  showAdTapCount: 200,
  episodeAdOrder: 'mopub_ad_first',
  currentAdMeta: {},
  source: null,
  userMarketItems: {}, // userId is the key and dataState is value
  userFans: {},
  userSettings: {}, // objectType-objectId is the first depth key, and key is second depth key
  storyAd: {
    ...makeDataState(),
    visited: [],
  },
  storyAdFull: {
    ...makeDataState(),
    isViewed: false,
  },
  latestMyStory: makeDataState(),
  vouchers: {}, // voucherType as key, list state
  voucherRecommendation: makeDataState(),
};

export default (state = initialState, action) => {
  switch (action.type) {
    // set access token
    case t.SET_ACCESS_TOKEN:
      return {
        ...state,
        accessToken: action.accessToken,
      };

    // social login or fetch user info
    case `${t.SOCIAL_SIGN_IN}_PENDING`:
    case `${t.FETCH_MY_INFO}_PENDING`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.PENDING,
        },
      };

    case `${t.SOCIAL_SIGN_IN}_SUCCESS`:
      return {
        ...state,
        firebaseIdToken: action.payload.result.customToken,
      };
    case `${t.FETCH_MY_INFO}_SUCCESS`:
      return {
        ...state,
        firebaseIdToken: action.meta.firebaseIdToken || state.firebaseIdToken,
        user: {
          ...state.user,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    case `${t.SOCIAL_SIGN_IN}_ERROR`:
    case `${t.FETCH_MY_INFO}_ERROR`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.ERROR,
        },
      };

    // change nickname
    case `${t.CHANGE_NICKNAME}_SUCCESS`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };
    case `${t.CHANGE_NICKNAME}_PENDING`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.PENDING,
        },
      };
    case `${t.CHANGE_NICKNAME}_ERROR`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.ERROR,
        },
      };

    // update me
    case t.UPDATE_ME:
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            ...action.meta.user,
          },
        },
      };

    // unregister
    case `${t.UNREGISTER_USER}_SUCCESS`:
      return {
        ...state,
        user: makeDataState(),
      };

    // reset user
    case t.RESET_USER:
      return {
        ...state,
        accessToken: null,
        firebaseIdToken: null,
        user: action.retry ? state.user : makeDataState(),
      };

    // toggle storage warnging
    case t.SHOW_STORAGE_WARNING:
      return {
        ...state,
        isStorageWarningShow: true,
      };

    case t.HIDE_STORAGE_WARNING:
      return {
        ...state,
        isStorageWarningShow: false,
      };

    // ab test
    case t.SET_EXPERIMENTS:
      return {
        ...state,
        abTest: {
          isDataReceived: true,
          ...action.data,
        },
      };

    case t.UPDATE_AB_TEST_DATA_RECEIVED:
      return {
        ...state,
        abTest: {
          ...state.abTest,
          isDataReceived: action.isDataReceived,
        },
      };

    case t.SET_SHOW_AD_TAP_COUNT:
      return {
        ...state,
        showAdTapCount: action.tapCount,
      };
    // adMob
    case t.SET_AD_LOAD_STATUS:
      return {
        ...state,
        adLoadStatus: {
          ...state.adLoadStatus,
          [action.adType]: action.status,
        },
      };

    // promotion
    case `${t.CHECK_PROMOTIONS}_SUCCESS`: {
      const { dailyBalloonPromotion } = action.payload.result;
      return {
        ...state,
        promotions: {
          dailyBalloonPromotion,
        },
      };
    }

    case t.SET_PROMOTIONS:
      return {
        ...state,
        promotions: {
          ...state.promotions,
          ...action.promotions,
        },
      };

    // fetch user market items
    case `${t.FETCH_USERS_MARKET_ITEMS}_PENDING`: {
      const { userIds } = action.meta;
      const newUserMarketItems = userIds.reduce((acc, value) => {
        if (state.userMarketItems[value]) {
          return acc;
        }

        return {
          ...acc,
          [value]: makeListState(),
        };
      }, {});

      return {
        ...state,
        userMarketItems: {
          ...state.userMarketItems,
          ...newUserMarketItems,
        },
      };
    }

    case `${t.FETCH_USERS_MARKET_ITEMS}_SUCCESS`: {
      const updatedUserMarketItems = action.payload.result.reduce(
        (acc, value) => {
          if (state.userMarketItems[value.userId].isLoaded) {
            return acc;
          }

          return {
            ...acc,
            [value.userId]: {
              ...state.userMarketItems[value.userId],
              isLoaded: true,
              list: value.marketItems,
            },
          };
        },
        {}
      );

      return {
        ...state,
        userMarketItems: {
          ...state.userMarketItems,
          ...updatedUserMarketItems,
        },
      };
    }

    // fetch user fans
    case `${t.FETCH_USER_FANS}_PENDING`: {
      const { userIds, targetUserId } = action.meta;
      const newUserFans = userIds.reduce((acc, value) => {
        if (state.userFans[value]) {
          return acc;
        }

        return {
          ...acc,
          [`${value}:${targetUserId}`]: makeDataState(),
        };
      }, {});

      return {
        ...state,
        userFans: {
          ...state.userFans,
          ...newUserFans,
        },
      };
    }

    case `${t.FETCH_USER_FANS}_SUCCESS`: {
      const { targetUserId } = action.meta;
      const { fanClub, users } = action.payload.result;
      const updatedUserFans = users.reduce((acc, value) => {
        const { id, isFan } = value;
        const key = `${id}:${targetUserId}`;

        return {
          ...acc,
          [key]: {
            ...state.userFans[key],
            isLoaded: true,
            data: { isFan, fanClub },
          },
        };
      }, {});

      return {
        ...state,
        userFans: {
          ...state.userFans,
          ...updatedUserFans,
        },
      };
    }

    // user settings
    case `${t.FETCH_USER_SETTING}_SUCCESS`: {
      const { objectType, objectId } = action.meta;
      const key = `${objectType}:${objectId}`;

      return {
        ...state,
        userSettings: {
          ...state.userSettings,
          [key]: snakeize(action.payload.result.setting),
        },
      };
    }

    case `${t.SET_USER_SETTING}_SUCCESS`: {
      const { objectType, objectId } = action.meta;
      const key = `${objectType}:${objectId}`;
      return {
        ...state,
        userSettings: {
          ...state.userSettings,
          [key]: {
            ...state.userSettings[key],
            ...snakeize(action.payload.result.setting),
          },
        },
      };
    }

    // ad story list
    case `${t.FETCH_STORY_AD}_SUCCESS`:
      return {
        ...state,
        storyAd: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
          visited: concatUnique(state.storyAd.visited, [action.meta.url]),
        },
      };

    // ad story full
    case t.SET_STORY_AD_FULL:
      return {
        ...state,
        storyAdFull: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.data,
          isViewed: false,
        },
      };

    case t.VIEW_STORY_AD_FULL:
      return {
        ...state,
        storyAdFull: {
          ...state.storyAdFull,
          isViewed: true,
        },
      };

    case t.RESET_STORY_AD_FULL:
      return {
        ...state,
        storyAdFull: {
          ...makeDataState(),
          isViewed: false,
        },
      };

    case `${t.FETCH_LATEST_MY_STORY}_SUCCESS`:
      return {
        ...state,
        latestMyStory: {
          isLoaded: true,
          status: STATUSES.SUCCESS,
          data: action.payload.result,
        },
      };

    // set value
    case t.SET_VALUE:
      return {
        ...state,
        [action.key]: action.value,
      };

    case t.SET_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case `${t.FETCH_VOUCHERS}_PENDING`:
      return {
        ...state,
        vouchers: {
          [action.meta.voucherType]: {
            status: STATUSES.PENDING,
            isLoaded: false,
            list: makeListState(),
          },
        },
      };
    case `${t.FETCH_VOUCHERS}_SUCCESS`:
      return {
        ...state,
        vouchers: {
          [action.meta.voucherType]: {
            status: STATUSES.SUCCESS,
            isLoaded: true,
            list: action.payload.result,
          },
        },
      };
    case `${t.FETCH_VOUCHER_RECOMMENDATION}_SUCCESS`:
      return {
        ...state,
        voucherRecommendation: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    default:
      return { ...state };
  }
};
export { initialState };



// WEBPACK FOOTER //
// ./src/app/redux/common/reducer.js